import {
  Component,
  DestroyRef,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AwFormFieldComponent,
  AwFormFieldLabelComponent,
  AwFormMessageComponent,
  AwInputDirective,
} from '@assetworks-llc/aw-component-lib';

@Component({
    selector: 'app-aw-text-form',
    imports: [
        AwFormFieldComponent,
        AwFormFieldLabelComponent,
        AwFormMessageComponent,
        // Without it AwInput is an inert attribute: inputs still look right (CCL styles them by element), but a
        // textarea never gets CCL's opentextarea class and stays one line tall.
        AwInputDirective,
        ReactiveFormsModule,
    ],
    templateUrl: './aw-text-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './aw-text-form.component.scss'
})
export class AwTextFormComponent implements OnInit, OnChanges {
  disable = input(false);
  readOnly = input(false);
  required = input(false);
  title = input('');
  value = input<string | null>('');
  placeholder = input('Enter Value');
  /** Renders a textarea, for names long enough to need wrapping. */
  multiline = input(false);
  /** Shows "Maximum characters is N (n/N)" and caps the field at N. */
  maxCharacters = input<number | null>(null);
  /** Characters the field accepts; anything else shows `patternMessage`. */
  allowedPattern = input<RegExp | null>(null);
  patternMessage = input('');

  textEntry = output<string | null>();

  private static nextId = 0;
  /** Ids for aria-describedby, unique per field on the page. */
  readonly hintId = `aw-text-form-${++AwTextFormComponent.nextId}-hint`;
  readonly errorId = this.hintId.replace(/-hint$/, '-error');

  userForm!: FormGroup;

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.userForm = new FormGroup({
      userInput: new FormControl({ value: this.value(), disabled: this.disable() }, this.validators()),
    });
    this.userForm.controls['userInput'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((text) => {
        this.textEntry.emit(text ?? '');
      });
  }

  /** Built from the inputs, so a later change to the limit or pattern takes effect (ngOnChanges). */
  private validators() {
    const max = this.maxCharacters();
    const pattern = this.allowedPattern();
    return [
      Validators.required,
      Validators.minLength(1),
      ...(max ? [Validators.maxLength(max)] : []),
      ...(pattern ? [Validators.pattern(pattern)] : []),
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value'] && !changes['value'].firstChange && this.userForm) {
      this.userForm.controls['userInput'].setValue(
        changes['value'].currentValue,
        { emitEvent: false }
      );
    }

    if (
      changes['disable'] &&
      !changes['disable'].firstChange &&
      this.userForm
    ) {
      const ctrl = this.userForm.controls['userInput'];
      changes['disable'].currentValue
        ? ctrl.disable({ emitEvent: false })
        : ctrl.enable({ emitEvent: false });
    }

    const rulesChanged = ['maxCharacters', 'allowedPattern'].some((key) => changes[key] && !changes[key].firstChange);
    if (rulesChanged && this.userForm) {
      const ctrl = this.userForm.controls['userInput'];
      ctrl.setValidators(this.validators());
      ctrl.updateValueAndValidity({ emitEvent: false });
    }
  }
}
